import { useQuery } from "@tanstack/react-query"
import { HandCoins } from "lucide-react"
import { useState } from "react"

import { api } from "@/api/cliente"
import { Exportar } from "@/componentes/exportar/Exportar"
import type { DocumentoExportable } from "@/componentes/exportar/modelo"
import { FichaOrden } from "@/componentes/ordenes/FichaOrden"
import { DialogoReintegro } from "@/componentes/tesoreria/DialogoReintegro"
import { aCentimos, comoMonto, monto } from "@/componentes/tesoreria/dinero"
import { Boton } from "@/componentes/ui/Boton"
import { Cajon } from "@/componentes/ui/Cajon"
import { EstadoCarga, EstadoError } from "@/componentes/ui/Estados"
import { Cabecera, Cuerpo, Fila, Tabla, TablaVacia, Td, Th } from "@/componentes/ui/Tabla"
import { tienePermiso } from "@/lib/autorizacion"
import { comoFecha } from "@/lib/fechas"
import { usarSesion } from "@/lib/sesion"

/**
 * El estado de cuenta de un proveedor: su saldo por moneda y las partidas que lo
 * explican. Lo abren Tesorería → «Saldos de proveedores» y la ficha del proveedor.
 *
 * ⚠ **Con saldo disponible de la empresa, desde aquí se registra que el proveedor lo
 * DEVOLVIÓ** (`DialogoReintegro`): un ingreso en una cuenta de esa moneda, con su
 * soporte. La otra forma de usarlo —descontarlo de otra orden suya— está en el modal de
 * pago de esa orden.
 */

export type Saldo = {
  divisa: string
  a_favor_proveedor: string
  disponible_empresa: string
  neto: string
}
export type SaldoProveedor = { proveedor_id: number; proveedor: string; saldos: Saldo[] }
export type Partida = {
  fecha: string
  tipo: "ORDEN" | "PAGO" | "AJUSTE_CIERRE" | "APLICACION" | "REINTEGRO" | "REEMBOLSO"
  /** Vacíos en una devolución: no cuelga de ninguna orden. */
  orden_id: number | null
  orden_codigo: string | null
  descripcion: string
  divisa: string
  efecto: string
  saldo: string
}
export type EstadoCuenta = SaldoProveedor & { partidas: Partida[] }

const TIPO: Record<Partida["tipo"], string> = {
  ORDEN: "Orden aprobada",
  PAGO: "Pago",
  AJUSTE_CIERRE: "Diferencia cerrada",
  APLICACION: "Saldo de la empresa aplicado",
  REINTEGRO: "El proveedor devolvió fondos",
  REEMBOLSO: "Ingreso del proveedor sin saldo que devolver",
}

export function EstadoDeCuenta({
  proveedorId,
  nombre,
  onCerrar,
}: {
  proveedorId: number
  nombre: string
  onCerrar: () => void
}) {
  const [orden, setOrden] = useState<number | null>(null)
  const [devolviendo, setDevolviendo] = useState<Saldo | null>(null)
  const usuario = usarSesion((e) => e.usuario)
  const puedeRegistrar = tienePermiso(usuario, "tesoreria:registrar")
  const estado = useQuery({
    queryKey: ["estado-cuenta-proveedor", proveedorId],
    queryFn: () => api.get<EstadoCuenta>(`/api/v1/saldos-proveedores/${proveedorId}`),
  })

  // La orden se abre EN LUGAR del estado de cuenta: dos cajones apilados no se leen.
  if (orden !== null) {
    return <FichaOrden ordenId={orden} onCerrar={() => setOrden(null)} desdeTesoreria />
  }

  return (
    <Cajon abierto titulo={`Estado de cuenta · ${nombre}`} onCerrar={onCerrar} ancho="amplio">
      {estado.isPending && <EstadoCarga etiqueta="Cargando el estado de cuenta…" />}
      {estado.isError && (
        <EstadoError
          titulo="No se pudo cargar el estado de cuenta."
          error={estado.error}
          onReintentar={() => estado.refetch()}
        />
      )}
      {estado.data && (
        <div className="space-y-5 text-sm">
          <div className="flex flex-wrap justify-end gap-2">
            <Exportar
              titulo={`Estado de cuenta · ${nombre}`}
              subtitulo={`Proveedor ${nombre}: saldo por moneda y partidas`}
              documento={() => documentoDeEstado(nombre, estado.data)}
            />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {estado.data.saldos.map((s) => {
              const neto = aCentimos(s.neto)
              return (
                <div key={s.divisa} className="tarjeta space-y-1 p-3">
                  <p
                    className="text-xs font-semibold uppercase"
                    style={{ color: "var(--fg-muted)" }}
                  >
                    {s.divisa}
                  </p>
                  <p className="text-lg font-semibold tabular-nums">
                    {comoMonto(neto < 0n ? -neto : neto)} {s.divisa}
                  </p>
                  <p style={{ color: "var(--fg-muted)" }}>
                    {neto > 0n
                      ? "a favor del proveedor: la agencia le debe"
                      : neto < 0n
                        ? "disponible de la empresa: el proveedor le debe"
                        : "sin saldo"}
                  </p>
                  {puedeRegistrar && aCentimos(s.disponible_empresa) > 0n && (
                    <Boton tamano="sm" onClick={() => setDevolviendo(s)}>
                      <HandCoins size={14} /> Registrar devolución de fondos
                    </Boton>
                  )}
                </div>
              )
            })}
          </div>

          <Tabla etiqueta="Partidas del estado de cuenta">
            <Cabecera>
              <Th>Fecha</Th>
              <Th>Orden</Th>
              <Th>Concepto</Th>
              <Th derecha>Movimiento</Th>
              <Th derecha>Saldo</Th>
            </Cabecera>
            <Cuerpo>
              {estado.data.partidas.length === 0 && (
                <TablaVacia colSpan={5}>
                  Este proveedor no tiene órdenes aprobadas ni pagadas.
                </TablaVacia>
              )}
              {estado.data.partidas.map((p) => (
                <Fila key={`${p.tipo}-${p.orden_id}-${p.fecha}-${p.descripcion}-${p.efecto}`}>
                  <Td numero>{comoFecha(p.fecha)}</Td>
                  <Td numero>
                    {p.orden_id === null ? (
                      "—"
                    ) : (
                      <button
                        type="button"
                        className="foco-cristal underline"
                        onClick={() => setOrden(p.orden_id)}
                      >
                        {p.orden_codigo}
                      </button>
                    )}
                  </Td>
                  <Td>
                    <span className="block">{TIPO[p.tipo]}</span>
                    <span className="block text-xs" style={{ color: "var(--fg-muted)" }}>
                      {p.descripcion}
                    </span>
                  </Td>
                  <Td derecha numero>
                    {p.tipo === "APLICACION" || p.tipo === "REEMBOLSO"
                      ? "—"
                      : `${monto(p.efecto)} ${p.divisa}`}
                  </Td>
                  <Td derecha numero>
                    {monto(p.saldo)} {p.divisa}
                  </Td>
                </Fila>
              ))}
            </Cuerpo>
          </Tabla>
          <p className="text-xs" style={{ color: "var(--fg-muted)" }}>
            Saldo positivo: la agencia le debe. Negativo: el proveedor le debe a la agencia.
          </p>
        </div>
      )}
      {devolviendo && (
        <DialogoReintegro
          proveedorId={proveedorId}
          proveedor={nombre}
          divisa={devolviendo.divisa}
          disponible={devolviendo.disponible_empresa}
          onCerrar={() => setDevolviendo(null)}
        />
      )}
    </Cajon>
  )
}

/** El estado de cuenta con la moneda en su columna: un saldo sin moneda no dice nada. */
function documentoDeEstado(nombre: string, estado: EstadoCuenta): DocumentoExportable {
  return {
    titulo: `Estado de cuenta · ${nombre}`,
    subtitulo: `Proveedor ${nombre}: saldo por moneda y partidas`,
    secciones: [
      {
        titulo: "Saldo por moneda",
        columnas: [
          { titulo: "Moneda", tipo: "texto" },
          { titulo: "A favor del proveedor", tipo: "monto" },
          { titulo: "Disponible de la empresa", tipo: "monto" },
          { titulo: "Neto", tipo: "monto" },
        ],
        filas: estado.saldos.map((s) => ({
          celdas: [
            s.divisa,
            Number(s.a_favor_proveedor),
            Number(s.disponible_empresa),
            Number(s.neto),
          ],
        })),
      },
      {
        titulo: "Partidas del estado de cuenta",
        columnas: [
          { titulo: "Fecha", tipo: "fecha" },
          { titulo: "Orden", tipo: "texto" },
          { titulo: "Tipo", tipo: "texto" },
          { titulo: "Concepto", tipo: "texto" },
          { titulo: "Moneda", tipo: "texto" },
          { titulo: "Movimiento", tipo: "monto" },
          { titulo: "Saldo", tipo: "monto" },
        ],
        filas: estado.partidas.map((p) => ({
          celdas: [
            p.fecha.slice(0, 10),
            p.orden_codigo,
            TIPO[p.tipo],
            p.descripcion,
            p.divisa,
            p.tipo === "APLICACION" || p.tipo === "REEMBOLSO" ? null : Number(p.efecto),
            Number(p.saldo),
          ],
        })),
      },
    ],
  }
}
